// Confirmation Modal for Trading operations
import React from 'react';
import { COLORS } from '../../utils/constants';
import { AlertTriangle, X, Check } from 'lucide-react';

const ConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'Подтверждение',
  message = 'Вы уверены?',
  confirmText = 'Подтвердить',
  cancelText = 'Отмена',
  type = 'warning', // 'warning', 'danger', 'info'
  loading = false,
}) => {
  if (!isOpen) return null;

  const colors = {
    warning: { bg: '#f0b90b20', border: '#f0b90b', icon: COLORS.primary },
    danger: { bg: '#f6465d20', border: '#f6465d', icon: COLORS.danger },
    info: { bg: '#0ecb8120', border: '#0ecb81', icon: COLORS.success },
  };

  const currentColors = colors[type] || colors.warning;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: '20px',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: COLORS.card,
          borderRadius: '16px',
          padding: '24px',
          maxWidth: '400px',
          width: '100%',
          border: `1px solid ${COLORS.border}`,
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
          animation: 'modalSlideIn 0.2s ease-out',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '16px',
          marginBottom: '20px',
        }}>
          {/* Icon */}
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            backgroundColor: currentColors.bg,
            border: `1px solid ${currentColors.border}40`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}>
            <AlertTriangle size={24} color={currentColors.icon} />
          </div>

          {/* Title & Message */}
          <div style={{ flex: 1 }}>
            <h3 style={{
              color: COLORS.text,
              fontSize: '18px',
              fontWeight: '700',
              margin: 0,
              marginBottom: '8px',
            }}>
              {title}
            </h3>
            <p style={{
              color: COLORS.textSecondary,
              fontSize: '14px',
              margin: 0,
              lineHeight: '1.5',
            }}>
              {message}
            </p>
          </div>

          {/* Close Button */}
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              padding: '4px',
              cursor: 'pointer',
              color: COLORS.textSecondary,
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = COLORS.background;
              e.target.style.color = COLORS.text;
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = COLORS.textSecondary;
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Actions */}
        <div style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end',
        }}>
          <button
            onClick={onClose}
            disabled={loading}
            style={{
              padding: '12px 24px',
              backgroundColor: COLORS.background,
              color: COLORS.text,
              border: `1px solid ${COLORS.border}`,
              borderRadius: '10px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.5 : 1,
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              if (!loading) e.target.style.backgroundColor = COLORS.border;
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = COLORS.background;
            }}
          >
            {cancelText}
          </button>

          <button
            onClick={onConfirm}
            disabled={loading}
            style={{
              padding: '12px 24px',
              backgroundColor: type === 'danger' ? COLORS.danger : COLORS.primary,
              color: type === 'danger' ? '#fff' : '#000',
              border: 'none',
              borderRadius: '10px',
              fontSize: '14px',
              fontWeight: '700',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s',
            }}
          >
            {loading ? (
              <>
                <span style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid transparent',
                  borderTopColor: 'currentColor',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite',
                }} />
                Выполнение...
              </>
            ) : (
              <>
                <Check size={16} />
                {confirmText}
              </>
            )}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes modalSlideIn {
          from {
            opacity: 0;
            transform: translateY(-20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ConfirmModal;
